<template>
  <div class="broadcast-report">
    <h4 class="broadcast-report-title">
      Relatório de envios (últimos 7 dias)
    </h4>

    <div class="broadcast-report-list">
      <BannerStatus
        title="Envios"
        :description="datatotals.sended"
        badge-color="#CCD3E6"
        :loading="!chart"
      />
      <BannerStatus
        title="Entregas"
        :description="datatotals.delivered"
        :percentage="datapercentages.delivered"
        badge-color="#052E80"
        :loading="!chart"
      />
      <BannerStatus
        title="Cliques"
        :description="datatotals.clicked"
        :percentage="datapercentages.clicked"
        badge-color="#197FF9"
        :loading="!chart"
      />
      <BannerStatus
        title="Respostas"
        :description="datatotals.answered"
        :percentage="datapercentages.answered"
        badge-color="#0FCF64"
        :loading="!chart"
      />
    </div>

    <div class="broadcast-report-graph" :class="{'loading-skeleton': !chart}">
      <BarChart
        v-if="chart"
        :data="datacollection"
        height="250px"
      />
    </div>

    <Banner color="violet">
      <svg
        xmlns="http://www.w3.org/2000/svg" width="24" height="24"
        viewBox="0 0 24 24" fill="none"
      >
        <path d="M13.3389 8.77027L14.4577 7.70664C14.5638 7.60805 14.6015 7.45759 14.5716 7.31738C14.5418 7.17718 14.4154 7.05501 14.2741 7.02262L12.7726 6.66L11.7079 5.541C11.6092 5.43482 11.4586 5.3971 11.3184 5.42682C11.1728 5.46577 11.0706 5.57892 11.0383 5.72015L10.6619 7.2247L9.54321 8.28833C9.43705 8.38692 9.39942 8.53738 9.42924 8.67759C9.4683 8.82312 9.58156 8.92541 9.72286 8.9578L11.2282 9.33497L12.293 10.454C12.3246 10.4845 12.347 10.5097 12.3839 10.531C12.4762 10.5843 12.5806 10.5954 12.6917 10.5735C12.8373 10.5345 12.9395 10.4214 12.9718 10.2802L13.3389 8.77027Z" fill="#551E64" />
        <path d="M9.54625 14.0045L9.98347 12.5189C10.0261 12.38 9.98347 12.2303 9.88749 12.1234C9.78085 12.0166 9.63156 11.9845 9.49293 12.0273L8 12.4548L6.51773 12.0166C6.3791 11.9738 6.22981 12.0166 6.12317 12.1128C6.01653 12.2196 5.98454 12.3693 6.0272 12.5082L6.45375 14.0045L6.01653 15.49C5.97388 15.629 6.01653 15.7786 6.11251 15.8855C6.21915 15.9924 6.36844 16.0244 6.50707 15.9817L7.98934 15.5435L9.4716 15.9817C9.50359 15.9924 9.54625 15.9924 9.5889 15.9924C9.69554 15.9924 9.79152 15.9496 9.87683 15.8748C9.98347 15.7679 10.0155 15.6183 9.9728 15.4794L9.54625 14.0045Z" fill="#551E64" />
        <path d="M17.084 16.0841L18.4465 15.341C18.5752 15.2727 18.651 15.1364 18.6588 14.9927C18.659 14.8414 18.576 14.713 18.4475 14.6451L17.0869 13.9056L16.3474 12.545C16.2795 12.4166 16.1435 12.3411 15.9998 12.3337C15.8485 12.3339 15.7198 12.4173 15.6516 12.546L14.9084 13.9085L13.5459 14.6516C13.4172 14.7199 13.3414 14.8561 13.3337 14.9998C13.3335 15.1511 13.4165 15.2796 13.545 15.3475L14.9055 16.087L15.645 17.4475C15.6601 17.4777 15.6903 17.508 15.7205 17.5382C15.7961 17.6137 15.8943 17.6514 16.0078 17.6588C16.1591 17.6586 16.2878 17.5752 16.356 17.4466L17.084 16.0841Z" fill="#551E64" />
      </svg>
      Envie mais transmissões para potencializar os resultados.
    </Banner>
  </div>
</template>

<script>
import Banner from '@/components/base/banner'
import BarChart from '@/components/base/charts/bar'

import BannerStatus from './banner-status'

export default {
  name: 'BroadcastReport',
  components: { Banner, BannerStatus, BarChart },
  data: () => ({
    chart: undefined
  }),
  computed: {
    zap() {
      return this.$store.state.zaps.zap || {}
    },
    datasets() {
      return Object.keys(this.chart || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .reduce(
          (acc, cur) => ({
            sended: [...acc.sended, this.chart[cur].sended],
            delivered: [...acc.delivered, this.chart[cur].delivered],
            clicked: [...acc.clicked, this.chart[cur].clicked],
            answered: [...acc.answered, this.chart[cur].answered]
          }),
          { sended: [], delivered: [], clicked: [], answered: [] }
        )
    },
    datatotals() {
      if (!this.chart)
        return { sended: 0, delivered: 0, clicked: 0, answered: 0 }

      return Object.entries(this.datasets).reduce(
        (acc, [key, values]) => ({
          ...acc,
          [key]: values.reduce((a, b) => a + b, 0)
        }),
        {}
      )
    },
    datapercentages() {
      if (!this.chart)
        return { sended: 0, delivered: 0, clicked: 0, answered: 0 }

      // eslint-disable-next-line prettier/prettier
      const percentage = value => (((value ?? 0) * 100) / (sended || 1)).toFixed(0)

      const sended = this.datatotals.sended
      const delivered = percentage(this.datatotals.delivered)
      const clicked = percentage(this.datatotals.clicked)
      const answered = percentage(this.datatotals.answered)

      return { delivered, clicked, answered }
    },
    datacollection() {
      const datacollection = {
        labels: Object.keys(this.chart || {})
          .sort((a, b) => new Date(a) - new Date(b))
          .map(label => {
            const isoDate = new Date(label).toISOString()
            const day = isoDate.substring(8, 10)
            const month = isoDate.substring(5, 7)
            return `${day}/${month}`
          }),
        datasets: [
          {
            label: 'Envios',
            backgroundColor: '#CCD3E6',
            data: [...this.datasets.sended]
          },
          {
            label: 'Entregas',
            backgroundColor: '#052E80',
            data: [...this.datasets.delivered]
          },
          {
            label: 'Cliques',
            backgroundColor: '#197FF9',
            data: [...this.datasets.clicked]
          },
          {
            label: 'Respostas',
            backgroundColor: '#0FCF64',
            data: [...this.datasets.answered]
          }
        ]
      }
      return datacollection
    }
  },
  async mounted() {
    try {
      const { data } = await this.$axios.get(
        `https://coordinator-api.clickzap.io/api/v1/schedulers/analytics/last7days`
      )
      this.chart = data
    } catch (_) {}
  }
}
</script>

<style lang="scss" scoped>
@import 'assets/scss/variables';

.broadcast-report {
  &-title {
    @include HeadingXSmall;
    font-weight: 600;
    font-family: $monserratFontFamily;
    color: $neutral500;

    margin-bottom: $scale900;
  }

  &-list {
    display: grid;
    grid-template-rows: repeat(3, 1fr);
    gap: $scale700;

    margin-bottom: $scale800;

    @include media-query($small-breakpoint) {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      grid-template-rows: 2fr;
    }

    @include media-query($medium-breakpoint) {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      grid-template-rows: 1fr;
    }
  }

  &-graph {
    margin-bottom: $scale600;
    border: 1px solid #e3e7ed;
    border-radius: 8px;
    padding: 24px 24px 16px;
    min-height: 277px;
  }

  .banner {
    @include LabelSmall;
    font-family: $monserratFontFamily;
    font-weight: 600;
    color: $purple400;

    display: flex;
    align-items: center;
    column-gap: 8px;
    padding: 16px 24px;
  }
}
</style>
